import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest, ApiError } from './config'
import type {
  Series,
  CreateSeriesRequest,
  UpdateSeriesRequest,
  SeriesEpisodesResponse,
  SeriesFeedbackResponse,
  PodcastShowSearchResult,
  GeneratedSeriesName,
  SeriesUserProfile,
  SubmitSeriesEpisodeFeedbackRequest,
} from '../types/series'

export const SERIES_QUERY_KEYS = {
  all: ['series'] as const,
  detail: (id: string) => ['series', id] as const,
  episodes: (id: string) => ['series', id, 'episodes'] as const,
  feedback: (id: string, page: number, pageSize: number) => ['series', id, 'feedback', page, pageSize] as const,
  rssUrl: (id: string) => ['series', id, 'rss-url'] as const,
  searchShows: (query: string) => ['series', 'search-shows', query] as const,
} as const

export const fetchSeriesRssUrl = async (id: string): Promise<{ rssUrl: string }> => {
  return apiRequest<{ rssUrl: string }>(`/api/series/${id}/rss-url`)
}

/**
 * Fetch all series for the current user
 */
export const useAllSeriesForCurrentUser = (): UseQueryResult<Series[], ApiError> => {
  return useQuery({
    queryKey: SERIES_QUERY_KEYS.all,
    queryFn: async (): Promise<Series[]> => {
      return apiRequest<Series[]>('/api/series')
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch a single series by ID
 */
export const useSeriesDetail = (id: string): UseQueryResult<Series, ApiError> => {
  return useQuery({
    queryKey: SERIES_QUERY_KEYS.detail(id),
    queryFn: async (): Promise<Series> => apiRequest<Series>(`/api/series/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch episodes for a series
 */
export const useSeriesEpisodes = (id: string, page: number = 1): UseQueryResult<SeriesEpisodesResponse, ApiError> => {
  return useQuery({
    queryKey: [...SERIES_QUERY_KEYS.episodes(id), page],
    queryFn: async (): Promise<SeriesEpisodesResponse> =>
      apiRequest<SeriesEpisodesResponse>(`/api/series/${id}/episodes?page=${page}&pageSize=20`),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch submitted feedback for a series
 */
export const useSeriesFeedback = (
  id: string,
  page: number = 1,
  pageSize: number = 100,
): UseQueryResult<SeriesFeedbackResponse, ApiError> => {
  return useQuery({
    queryKey: SERIES_QUERY_KEYS.feedback(id, page, pageSize),
    queryFn: async (): Promise<SeriesFeedbackResponse> =>
      apiRequest<SeriesFeedbackResponse>(`/api/series/${id}/feedback?page=${page}&pageSize=${pageSize}`),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Get RSS feed URL for a series
 */
export const useSeriesRssUrl = (id: string): UseQueryResult<{ rssUrl: string }, ApiError> => {
  return useQuery({
    queryKey: SERIES_QUERY_KEYS.rssUrl(id),
    queryFn: async (): Promise<{ rssUrl: string }> => fetchSeriesRssUrl(id),
    enabled: !!id,
    staleTime: Infinity,
  })
}

/**
 * Search for podcast shows
 */
export const useSearchPodcastShows = (query: string): UseQueryResult<PodcastShowSearchResult[], ApiError> => {
  return useQuery({
    queryKey: SERIES_QUERY_KEYS.searchShows(query),
    queryFn: async (): Promise<PodcastShowSearchResult[]> =>
      apiRequest<PodcastShowSearchResult[]>(`/api/series/search-shows?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Create a new series
 */
export const useCreateSeries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: CreateSeriesRequest): Promise<Series> => {
      return apiRequest<Series>('/api/series', {
        method: 'POST',
        body: JSON.stringify(request),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.all })
    },
  })
}

/**
 * Update series settings
 */
export const useUpdateSeries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...request }: UpdateSeriesRequest & { id: string }): Promise<Series> => {
      return apiRequest<Series>(`/api/series/${id}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.all })
      queryClient.setQueryData(SERIES_QUERY_KEYS.detail(data.id), data)
    },
  })
}

/**
 * Delete a series
 */
export const useDeleteSeries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      return apiRequest<void>(`/api/series/${id}`, {
        method: 'DELETE',
      })
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.all })
      queryClient.removeQueries({ queryKey: SERIES_QUERY_KEYS.detail(id) })
    },
  })
}

/**
 * Pause a series
 */
export const usePauseSeries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<{ id: string; status: string }> => {
      return apiRequest<{ id: string; status: string }>(`/api/series/${id}/pause`, {
        method: 'POST',
      })
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.all })
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.detail(id) })
    },
  })
}

/**
 * Resume a series
 */
export const useResumeSeries = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<{ id: string; status: string; nextEpisodeDueAt: string }> => {
      return apiRequest<{ id: string; status: string; nextEpisodeDueAt: string }>(`/api/series/${id}/resume`, {
        method: 'POST',
      })
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.all })
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.detail(id) })
    },
  })
}

/**
 * Manually trigger episode generation
 */
export const useGenerateSeriesEpisodeNow = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<{ message: string }> => {
      return apiRequest<{ message: string }>(`/api/series/${id}/generate-now`, {
        method: 'POST',
      })
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.episodes(id) })
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.detail(id) })
    },
  })
}

/**
 * Submit feedback for a series episode
 */
export const useSubmitSeriesEpisodeFeedback = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...request }: SubmitSeriesEpisodeFeedbackRequest & { id: string }): Promise<{ id: string }> => {
      return apiRequest<{ id: string }>(`/api/series/${id}/feedback`, {
        method: 'POST',
        body: JSON.stringify(request),
      })
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['series', id, 'feedback'] })
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.detail(id) })
    },
  })
}

/**
 * Retry a failed episode
 */
export const useRetryFailedSeriesEpisode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ podcastId }: { seriesId: string; podcastId: string }): Promise<{ message: string }> => {
      return apiRequest<{ message: string }>(`/api/series/episodes/${podcastId}/retry`, {
        method: 'POST',
      })
    },
    onSuccess: (_, { seriesId }) => {
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.episodes(seriesId) })
      queryClient.invalidateQueries({ queryKey: SERIES_QUERY_KEYS.detail(seriesId) })
    },
  })
}

/**
 * Generate a series name from interests
 */
export const useGenerateSeriesName = () => {
  return useMutation({
    mutationFn: async (params: { interests: string; languageIso: string }): Promise<GeneratedSeriesName> => {
      return apiRequest<GeneratedSeriesName>('/api/series/generate-name', {
        method: 'POST',
        body: JSON.stringify({ interests: params.interests, languageIso: params.languageIso }),
      })
    },
  })
}

export interface ClarifyMessage {
  role: 'assistant' | 'user'
  content: string
}

export interface ClarifyRequest {
  interests: string
  useCase?: string | null
  conversationHistory: ClarifyMessage[]
}

export interface ClarifyResponse {
  message: string
  isComplete: boolean
  userProfile?: SeriesUserProfile | null
}

export interface NeedsClarificationRequest {
  interests: string
  useCase?: string | null
}

export interface NeedsClarificationResponse {
  needsClarification: boolean
  reason: string
}

/**
 * Check if user interests need clarification
 */
export const useCheckNeedsClarification = () => {
  return useMutation({
    mutationFn: async (request: NeedsClarificationRequest): Promise<NeedsClarificationResponse> => {
      return apiRequest<NeedsClarificationResponse>('/api/series/needs-clarification', {
        method: 'POST',
        body: JSON.stringify(request),
      })
    },
  })
}

/**
 * Get clarifying questions for user interests
 */
export const useClarifyInterests = () => {
  return useMutation({
    mutationFn: async (request: ClarifyRequest): Promise<ClarifyResponse> => {
      return apiRequest<ClarifyResponse>('/api/series/clarify', {
        method: 'POST',
        body: JSON.stringify(request),
      })
    },
  })
}
